import L from 'leaflet'
import {
  cloneDeep,
  hasIn,
  isEmpty,
  isNull,
  merge,
  throttle,
} from 'lodash-unified'
import { ShipxyOptions } from '@map-sdk/sdk/config'
import { CanvasShipUtils } from '@map-sdk/sdk/utils/CanvasShipUtils'
import { GPS } from '@map-sdk/sdk/utils/GPS'
import { CommUtils } from '@map-sdk/sdk/utils/CommUtils'
import { canvasShipSymbol } from '@map-sdk/sdk/CanvasShipSymbol'
import AreaShipsDataGridSymbol from '@map-sdk/sdk/utils/AreaShipsDataGridSymbol'
import { CanvasShip } from '@map-sdk/sdk/model/CanvasShip'
import type { MyMap } from '@map-sdk/sdk/ShipxyAPISDK'
import type { CustomMap } from '@map-sdk/sdk/utils/CanvasShipUtils'
import type { Layer } from 'leaflet'

interface Ship {
  // 自定义船舶名称，用于区别显示或其他个性化用途
  custom_name: string
  // 船舶所属分组或集群的名称
  group_name: string
  // 关于船舶的额外备注或说明信息
  remark?: string
  // 数据来源编号，指明船舶数据的提供源头
  source: number
  // 实际数据来源编号，更为具体的来源标识
  source_real: number
  // MMSI号码，船舶独一无二的九位数字识别码
  mmsi: string
  // 船舶在系统内的唯一标识ID
  shipid: string
  // 船舶通信终端ID，默认未定义
  terminalid: undefined
  // 船舶通信终端类型，默认未定义
  terminaltype: undefined
  // 船舶当前地理位置的经度
  lng: number
  // 船舶当前地理位置的纬度
  lat: number
  // 船首向（Heading），表示船舶艏部指向的角度（单位通常为度）
  hdg: number | undefined
  // 船首向数据来源，默认未定义
  hdg_source: undefined
  // 方向角（Course Over Ground, COG），船舶实际行驶方向相对于地球表面的角度
  cog: number | undefined
  // 地面速度（Speed Over Ground, SOG），船舶在地面上的实际运动速度
  sog: number | undefined
  // 航向改变率，即船舶航向变化的速度
  rot: number | undefined
  // 船舶导航状态代码，表示船舶当前导航系统的运行状态
  navistatus: string
  // 最后一次动态更新的时间戳，记录船舶状态最后更新的时间
  lastdyn: number
  // 标记最后一次动态更新是否有效
  lastdyn_active: boolean
  // 船舶类型代码，标识船舶类别
  type: number | string
  // 船舶的官方名称
  name: string
  // 船舶的中文名称，默认未定义
  cnname: undefined
  // 船舶的英文名称，默认未定义
  nameen: undefined
  // 船舶呼号，默认未定义
  callsign: undefined
  // 国际海事组织编号（IMO号），船舶的国际唯一身份识别码
  imo: string
  // 船舶长度
  length: number
  // 船舶宽度
  width: number
  left: number //
  // 船迹长度，记录船舶航行轨迹的总长度
  trail: number
  // 吃水深度，船舶底部到水面的距离
  draught: number
  // 目的地，默认未定义
  dest: undefined
  // 预计到达时间，默认未定义
  eta: undefined
  // 船舶最后一次定位的时间戳，默认未定义
  laststa: undefined
  // 船舶的历史轨迹记录，目前未定义任何类型
  tracks: never[]
  // 标识船舶当前是否处于停止状态
  istop: boolean
  // 船舶在地图或其他界面中的颜色标识
  color: string
  // 船舶状态代码，用于区分不同运营状态
  // 0 表示对象处于某种非活动状态或初始化状态。延迟<30分钟
  // 1 表示对象处于中间状态，可能是警告状态或需要用户注意的状态 30分钟<延迟<2个小时
  // 2 表示对象处于完全活动状态或正常工作状态 延迟> 2小时
  state: number
  // 真实地理坐标系下的船舶中心点坐标，默认未定义
  pointCenterReal: undefined
  // GPS坐标系下的船舶中心点坐标，默认未定义
  pointCenterGPS: undefined
  // 真实地理坐标系下的船舶经纬度坐标，默认未定义
  latlngReal: undefined
  // GPS坐标系下的船舶经纬度坐标，默认未定义
  latlng: undefined
  // 可能在二维平面上表示的船舶横坐标
  x: number
  // 可能在二维平面上表示的船舶纵坐标
  y: number
  // 船舶在地图上绘制时的线宽
  lineWidth: number
  // 船舶图标旋转角度
  rotate: number
  // 船体区域信息，默认未定义
  bodyArea: undefined
  // 船舶头部在某个坐标系下的横坐标，默认未定义
  head_x: undefined
  // 船舶头部在某个坐标系下的纵坐标，默认未定义
  head_y: undefined
  // 船舶类型的另一种表示方式
  shiptype: number | string
  // 数据功率或者数据强度，可能与船舶信号传输有关
  datapower: number
  // 是否为某种特定类型（yh的具体含义未给出）
  is_yh: boolean
  // 标识船舶是否处于监控状态
  is_monitor: boolean
  // 标识船舶监控状态是否在线
  monitorOnline: boolean
  // 标识船舶是否在中国境外
  outCN: boolean
  // 偏移量，可能用于计算船舶位置或其他属性的偏移
  offset: number
  // 船旗国编号
  country: number
  // 文本颜色，可能用于标注船舶信息的颜色
  txt_color: string
  // 新类型标识，可能用于区分不同分类或状态
  newtype: string
  // 船舶线条颜色，默认未定义
  lineColor: undefined
  // 船舶高度线颜色，默认未定义
  heightLineColor: undefined
  // 附加选项或配置信息，默认未定义
  options: undefined

  // 所属海域ID，标识船舶所在的海洋区域
  seaareaid: string

  // 是否显示时间标签，默认未定义
  isShowLableTime: undefined

  // 是否显示船舶标签，默认未定义
  isShowLabel: undefined

  // 船舶标签文本，默认未定义
  labelText: undefined

  // 标识船舶是否出现在雷达监测范围内
  is_radar: boolean

  // 雷达设备ID，默认未定义
  radarid: undefined

  // 雷达数据来源，默认未定义
  radarSource: undefined

  // 雷达目标信息，默认未定义
  radarTarget: undefined

  // GPS提供的船舶最新经纬度信息中的纬度
  gps_lat: number

  // GPS提供的船舶最新经纬度信息中的经度
  gps_lng: number

  // GPS最后一次更新
  gps_lastdyn: string
  // 服务器时间
  serverTime: undefined
  shipxy_tooltip?: L.Tooltip
  //表示船舶在某个数组或其他集合中的位置索引
  arr_inde: number
  //该船舶是否被用户选择或标记。如果为 true，则表示当前船舶在用户界面操作中已被选中；若为 false，则未被选中。
  isSelected: boolean
}

// 假设以下是一些相关的数据类型定义

interface SpecialShip extends Ship {
  color: string
  istop: boolean
}

interface LabelData {
  symbol: any
  calcLabel: any // 需要具体的类型定义
  sourceType: any
  labelImage: any // 需要具体的类型定义
  labelText: string
  txtColor: string
  font: string
  lineColor: string
  lineFillColor: string | null
  obliqueLineColor: string | null
  lableRotate: any // 需要具体的类型定义
}
export interface CanvasShipServiceOptions {
  isAutoUpdateSrvtime?: boolean // 是否自动更新srvtime，默认：true
  enableAreaShip?: boolean // 是否自动刷新区域船，默认：true
  enableFleetShip?: boolean // 是否自动刷新关注船，默认：true
  enableDShip?: boolean // 是否自动刷新D+船，默认：false
  enableTracks?: boolean // 是否显示近期轨迹，默认：true
  enableSelectedShip?: boolean // 是否自动刷新选中船，默认：false
  dShipColor?: string // D+船颜色，默认："#ff6347"
  topLabelZoom?: number // 顶层船从第几级开始显示标签，默认：1
  generalLabelZoom?: number // 普通船从第几级开始显示标签，默认：13
  weixingLabelZoom?: number // 卫星船从第几级开始显示标签，默认：1
  delayTime?: number // 刷新间隔时间，单位：毫秒
  bounds?: L.LatLngBoundsExpression // 指定区域船显示的区域，默认是地图可视范围
  zoomLevel_base?: number // 默认值：9，zoom大于该值，用于船舶分组：baseShips
  lableRotate?: number | undefined // 标签旋转角度，默认值：undefined
  lableTxtColorMapTypeDark?: string[] // 船舶名称文字颜色（针对深色瓦片类型），默认：["MT_SATELLITE", "MT_ESRI", "MT_WEIRUANWEIXING"]
  lableTxtColor?: string[] // 船舶名称文字颜色，默认值：["#000","#fff"]
  lableFont?: string[] // 船舶名称文字字体，默认值：["'600 12px Arial'", "'500 12px Arial'"]
  lableLineColor?: string[] // 船舶名称文字线框颜色，默认值：["#000","#000"]
  monitorShips?: number[] // 安装有视频监控的船舶MMSI列表
  getAreaShipsCallBack?: (ships: CanvasShip[]) => void // 区域船外部数据回调，返回CanvasShip对象数组，默认值：undefined
  drawShipsEndCallBack?: (shipCount: Record<string, any>) => void // 绘制区域船后回调，返回对象：{count:number}
  shipSizeZoom?: number // 船舶缩放比例，默认值：1
  shipOptions?: any // 船舶模型设置（假设为复杂对象结构，这里未给出具体类型）
  tooltip_fields?: any // tooltip 显示字段设置（假设为复杂对象结构，这里未给出具体类型）
  drawBaseShipsGridZoom?: number[] // 级别，0.1°网格显示船舶数量限制级别，默认：[]
  drawBaseShipsGridMax?: number[] // 数量，0.1°网格显示船舶数量限制数量，默认：[]，大于0生效
  drawBaseShipsGridSize?: number[] // 缩放级别对应的网格大小，默认：[]，默认：0.5
  drawBaseShipsGridMaxCount?: number // 绘制船大于该数量，开启网格抽稀绘制，默认：0，大于0生效
  areaShipsDataGridSize?: number // 网格化存储，网格大小，默认：0.05
  // 开启右键菜单配置项
  openRightMenu?: {
    selectShip: boolean // 右键的时候是否选中船舶
    isUse: boolean // 是否开启右键菜单
  }
}

export interface CanvasShipMap extends CustomMap {
  _hoverShipId: any
  offsetC: any
  _selShipId: Record<string, any>
  _size: L.Point
  _unselectedShipListener?: (params?: any) => void
  shipsService?: CanvasShipService
}

class CanvasShipService extends L.Class {
  _filterOptions: {
    shipType: string
    shipSog: string
    shipCustomSog: never[]
    shipNaviStatus: string
    shipLength: number[][]
    shipHdg: number[][]
    shipCountry: string
    validIMO: boolean
  }
  options: {
    isAutoUpdateSrvtime: boolean
    enableAreaShip: boolean
    enableFleetShip: boolean
    enableTracks: boolean
    enableDShip: boolean
    enableSelectedShip: boolean
    requestCallback: {
      getShip: null
      getAreaShip: null
      getDPlusShip: null
      getFleetShip: null
    }
    dShipColor: string
    topLabelZoom: number
    generalLabelZoom: number
    weixingLabelZoom: number
    delayTime: number
    offsetLastdyn: number
    bounds: undefined
    getAreaShipsCallBack: undefined
    zoomLevel_base: number
    labelLanguage: string
    lableRotate: Record<string, any>
    lableFont: Record<string, any>
    lableTxtColorMapTypeDark: string[]
    lableTxtColor: Record<string, any>
    lableLineColor: Record<string, any>
    lableLinefillColor: Record<string, any>
    monitorShips: string[]
    obliqueLineColor: Record<string, any>
    resetShipColorCallBack?: (params: any) => void
    drawShipsEndCallBack?: (params: any) => void
    drawShipsBeforeCallBack?: (params: any) => any
    drawRequestShipsBeforeCallback?: (params: any) => any
    shipSizeZoom: number
    shipOptions: {
      sizeZoom: number
      lineColor: string
      heightLineColor: string
    }
    tooltip_fields: string[]
    areaShipsDataGridSize: number
    drawBaseShipsGridZoom: number[]
    drawBaseShipsGridMax: never[]
    drawBaseShipsGridSize: never[]
    drawBaseShipsGridMaxCount: number
    isShowLableTime: undefined
    isShowLabel: boolean
    radarOptions: {
      color: string
      lineColor: string
      heightLineColor: string
      lineWidth: number
      iconSize: number
    }
    openRightMenu?: {
      selectShip: boolean
      isUse: boolean
    }
    netsondeOptions: {
      show: boolean
      color: string
      lineColor: string
      heightLineColor: string
      lineWidth: number
      iconSize: number
    }
    color: string
    maptype: string
    labelImages: Record<string, any>
  }
  _mouseMoveTimer: any | undefined
  _timerOpen: boolean
  _timer!: any | null
  _map_move_time_num: number
  _canvas_mousedown: boolean
  _moveendTimer: null
  _labelImages: {
    common?: boolean
    satellite?: boolean
    dplus?: boolean
    beidou?: boolean
    monitor?: boolean
  }
  _map: MyMap
  _srvTime: number
  _layerGroup: L.FeatureGroup<any>
  _zoomLevel: any
  _areaShipIsShow: boolean
  _noSelShipIsShow: boolean
  _grid_ships: any
  _requestAreaShips: CanvasShip[]
  _requestAreaShipsLoading: boolean
  _requestAreaShips_timer: undefined
  _requestAreaShips_num: number
  _selectedShipUpdateListener?: ((params: any) => void) | null
  _selectedShipListener?: ((params: any) => void) | null
  _rightContentMenu?:
    | ((ship: Ship | null, style?: Record<string, any>) => void)
    | null
  _isRightContentMenuOpen = false // 标识是否已经弹出右键菜单
  _fleetShips: Record<string, CanvasShip> //FleetShip是船队船舶数据结构
  _DShips: Record<string, CanvasShip> // DSship是特定类型船舶数据结构
  _specialShips: Record<string, SpecialShip> // SpecialShip是特殊船舶数据结构
  _DShips_key: string
  _beidou_ships: string[]
  _viewShips: CanvasShip[]
  _labels: LabelData[]
  _labels_time: number
  _drawLables!: LabelData[]
  _scode: number
  _continue: number
  _monitorShipsHash: Record<string, any>
  _drawCount: number
  _topShipsCount: number
  _baseShipsCount: number
  _baseShipsCount_chouxi: number
  _clrState: string[]
  _timer_area_num: number
  _delayTime: number
  _grid_ships_view_filter!: Map<string, any>
  _tooltip_txt_templater: {
    name: string
    cnname: string
    mmsi: string
    imo: string
    callsign: string
    sog: string
    cog: string
    hdg: string
    draught: string
    dest: string
    lastdyn: string
    navistatus: string
    type: string
    rot: string
    lat: string
    lng: string
    seaareaid: string
  }
  _tooltip!: Layer | null

  constructor(map: MyMap, options?: CanvasShipServiceOptions) {
    super()

    // 设置过滤船舶信息
    this._filterOptions = {
      shipType: '',
      shipSog: '0,1',
      shipCustomSog: [],
      shipNaviStatus: '',
      shipLength: [
        [0, 40],
        [41, 80],
        [81, 160],
        [161, 240],
        [241, 320],
        [321, 9999],
      ],
      shipHdg: [
        [0, 360],
        [511, 511],
      ],
      shipCountry: '0,1,2',
      validIMO: false,
    }
    this.options = {
      isAutoUpdateSrvtime: true,
      enableAreaShip: true,
      enableFleetShip: true,
      enableTracks: false,
      enableDShip: false,
      enableSelectedShip: false,
      requestCallback: {
        getShip: null,
        getAreaShip: null,
        getDPlusShip: null,
        getFleetShip: null,
      },
      dShipColor: '#ff6347',
      topLabelZoom: 1,
      generalLabelZoom: 13, // 自动显示标牌层级
      weixingLabelZoom: 1,
      delayTime: 15000,
      offsetLastdyn: 0,
      bounds: undefined,
      getAreaShipsCallBack: undefined,
      zoomLevel_base: 2, // 控制在第几级显示
      labelLanguage: ShipxyOptions.language.language,
      lableFont: ['600 12px Arial', '500 12px Arial'],
      lableTxtColorMapTypeDark: [
        'MT_SATELLITE',
        'MT_ESRI',
        'MT_WEIRUANWEIXING',
      ],
      lableRotate: [4, 4],
      lableTxtColor: ['#fff', '#000'],
      lableLineColor: ['#000', '#000'],
      lableLinefillColor: [
        'rgba(255, 255, 255, 0.8)',
        'rgba(255, 255, 255, 0.8)',
      ],
      monitorShips: [],
      obliqueLineColor: ['#000', '#000'],
      shipSizeZoom: 1,
      shipOptions: {
        sizeZoom: 1,
        lineColor: '#000',
        heightLineColor: '#000',
      },
      tooltip_fields: ['name', 'callsign', 'imo', 'mmsi', 'lastdyn'],
      areaShipsDataGridSize: 0.05,
      drawBaseShipsGridZoom: [],
      drawBaseShipsGridMax: [],
      drawBaseShipsGridSize: [],
      drawBaseShipsGridMaxCount: 0,
      isShowLableTime: undefined,
      isShowLabel: true,
      radarOptions: {
        color: '#ffa500',
        lineColor: '#000000',
        heightLineColor: '#000000',
        lineWidth: 1,
        iconSize: 6,
      },
      openRightMenu: {
        isUse: true,
        selectShip: true,
      },
      netsondeOptions: {
        show: true,
        color: 'rgba(255,255,0,0.2)',
        lineColor: 'rgba(255,255,0,1)',
        heightLineColor: 'rgba(255,255,0,1)',
        lineWidth: 1,
        iconSize: 14,
      },
      color: '',
      maptype: '',
      labelImages: {},
    }
    this._timerOpen = false
    this._map_move_time_num = 0
    this._canvas_mousedown = false
    this._moveendTimer = null
    this._labelImages = {}
    this._map = map
    this._map._selShipId = {}
    this._srvTime = Date.now() / 1000
    L.Util.extend(this.options, options)
    this._layerGroup = L.featureGroup().addTo(map)
    this._zoomLevel = this._map.getZoom()
    this._areaShipIsShow = true
    this._noSelShipIsShow = true
    this._grid_ships = AreaShipsDataGridSymbol(this._map, {
      grid_size: this.options.areaShipsDataGridSize,
    })
    this._requestAreaShips = []
    this._requestAreaShipsLoading = false
    this._requestAreaShips_timer = undefined
    this._requestAreaShips_num = 0
    this._fleetShips = {}
    this._DShips = {}
    // 特殊船舶
    this._specialShips = {}
    this._DShips_key = 'd_'
    // 北斗船舶
    this._beidou_ships = []
    this._viewShips = []
    this._labels = []
    this._labels_time = 0
    this._scode = 0
    this._continue = 0
    this._monitorShipsHash = {}
    this._tooltip_txt_templater = {
      name: '船名：{1}<br/>',
      cnname: '中文船名：{1}<br/>',
      mmsi: 'MMSI：{1}<br/>',
      imo: 'IMO：{1}<br/>',
      callsign: '呼号：{1}<br/>',
      sog: '航速：{1}节<br/>',
      cog: '航迹向：{1}度<br/>',
      hdg: '船首向：{1}度<br/>',
      draught: '吃水：{1}米<br/>',
      dest: '目的地：{1}<br/>',
      lastdyn: '时间：{1}<br/>',
      navistatus: '状态：{1}<br/>',
      type: '类型：{1}<br/>',
      rot: '旋转角速度：{1}度/秒<br/>',
      lat: '纬度：{1}<br/>',
      lng: '经度：{1}<br/>',
      seaareaid: '海区：{1}<br/>',
    }

    this._drawCount = 0
    this._topShipsCount = 0
    this._baseShipsCount = 0
    this._baseShipsCount_chouxi = 0
    this._clrState = ['#ffff66', '#00cc00', '#999999']
    this._timer_area_num = 1
    this.resizeHTML()
    this._labelImages = this._getLabelImages()
    this._map.on(
      'move',
      throttle((e) => {
        this._map_move_function()
        this._drawShips(e)
      }, 100)
    )
    this._map.on(
      'moveend',
      throttle((e) => {
        this._zoomLevel = this._map.getZoom()
        this._map_move_end_function(e)
      }, 100)
    )
    this._map.on('baselayerchange', (shipData) => {
      this._drawShips(shipData)
    })
    this._delayTime = Math.max(5000, this.options.delayTime)
    if (
      this.options.enableAreaShip ||
      this.options.enableFleetShip ||
      this.options.enableSelectedShip
    )
      this._timerStart()
  }

  resizeHTML() {
    this._drawCount = 0
    this._topShipsCount = 0
    this._baseShipsCount = 0
    this._baseShipsCount_chouxi = 0
    const { clientWidth, clientHeight } = this._map.getContainer()
    this._map._size = new L.Point(clientWidth || 0, clientHeight || 0)
    const panes = this._map.getPanes()
    panes.divCanvasShipsPane && panes.divCanvasShipsPane.remove()
    const divCanvasShips = L.DomUtil.create(
      'div',
      'div-canvas-ships',
      panes.overlayPane
    )
    divCanvasShips.style.zIndex = '150'
    panes.divCanvasShipsPane = divCanvasShips
    const canvasShips = L.DomUtil.create(
      'canvas',
      'canvas-ships',
      panes.divCanvasShipsPane
    )
    canvasShips.width = this._map.getSize().x
    canvasShips.height = this._map.getSize().y
    canvasShips.addEventListener(
      'mousedown',
      () => {
        this._canvas_mousedown = true
      },
      false
    )

    canvasShips.addEventListener(
      'mouseup',
      () => {
        this._canvas_mousedown = false
      },
      false
    )

    canvasShips.addEventListener(
      'mousemove',
      (e) => {
        this._drawCount
          ? this._drawCount > 0 &&
            !this._canvas_mousedown &&
            this._mousemoveFunction(e, this)
          : this._closeTooltip()
      },
      false
    )
    if (this.options.openRightMenu?.isUse) {
      // 在容器上添加右键点击事件监听
      canvasShips.addEventListener(
        'contextmenu',
        (event: any) => {
          event.preventDefault() // 阻止默认的右键菜单
          const ship: Ship = this.getShipByEvent(event)
          if (!ship) return
          // 当开启右键定位船舶的时候 绘制
          if (this.options.openRightMenu?.selectShip)
            this.setSelectedShip(ship.shipid)
          // 在这里可以添加自定义的上下文菜单代码

          const positionPopup = (
            event: { layerX: number; layerY: any },
            popup: HTMLElement
          ) => {
            // 首先，确保弹出层是可见的以测量其尺寸
            popup.style.display = 'block'

            // 获取弹出层的尺寸
            const popupWidth = popup.offsetWidth
            const popupHeight = popup.offsetHeight

            // 计算弹出层的初始位置
            let left = event.layerX + 10
            let top = event.layerY

            // 获取屏幕的尺寸
            const { x: windowWidth, y: windowHeight } = this._map.getSize()

            // 调整位置以防止弹出层超出屏幕右侧或底部
            if (event.layerX + popupWidth > windowWidth) {
              left = windowWidth - popupWidth - 100
            }
            if (event.layerY + popupHeight > windowHeight) {
              top = windowHeight - popupHeight - 30
            }

            return { left, top }
          }
          const { left, top } = positionPopup(
            event,
            document.querySelector('.context-menu') as HTMLElement
          )
          ship &&
            this._rightContentMenu &&
            this.disableMap() &&
            this._rightContentMenu(ship, {
              left: `${left}px`,
              top: `${top}px`,
              display: 'block',
            })
          this._isRightContentMenuOpen = true
        },
        false
      )
    }

    canvasShips.addEventListener('click', (e) => {
      this.closeRightMenu()
      if (this._drawCount) {
        const mouseInArr = CanvasShipUtils.mouseInArr(
          this._map.getPanes().canvasShipsPane,
          e,
          this._viewShips
        )
        mouseInArr
          ? this.setSelectedShip(mouseInArr.shipid)
          : this._map._unselectedShipListener &&
            setTimeout(() => {
              this._map._unselectedShipListener &&
                this._map._unselectedShipListener()
            }, 50)
      }
    })

    this._map.getPanes().canvasShipsPane = canvasShips
    this._drawShips()
  }
  closeRightMenu() {
    if (this._isRightContentMenuOpen) {
      this._rightContentMenu &&
        this.enableMap() &&
        this._rightContentMenu(null, { display: 'none' })
      if (this.options.openRightMenu?.isUse) this.cancelSelectedShip()
      this._isRightContentMenuOpen = false
    }
  }
  disableMap() {
    this._map.dragging.disable()
    this._map.touchZoom.disable()
    this._map.scrollWheelZoom.disable()
    return true
  }
  enableMap() {
    this._map.dragging.enable()
    this._map.touchZoom.enable()
    this._map.scrollWheelZoom.enable()
    return true
  }
  _getLabelImages() {
    return {
      common: false,
      satellite: true,
      dplus: true,
      monitor: true,
      beidou: false,
    }
  }
  _map_move_end_function(e?: L.LeafletEvent) {
    console.log(`_map_move_end_function time(start):${Date.now()}`)
    const startTime = Date.now()
    this._map_move_time_num = Date.now()
    this._map_move_function()
    this._drawShips(e)

    if (this.options.enableAreaShip) {
      Promise.all([
        this._requestAreaShips_v3(),
        this._requestAreaShips?.length > 0 && this._drawRequestShips(this),
      ]).then(() => {
        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(() => {
            this._drawRequestShips(this)
          })
        } else {
          setTimeout(() => {
            this._drawRequestShips(this)
          }, 800)
        }
      })
    }

    console.log(`_map_move_end_function time(end):${Date.now() - startTime}`)
  }

  private getMapTransform(map: any): string {
    const style = map._mapPane.getAttribute('style')
    let transformCss = ''
    const translateKey = L.Browser.ie ? 'translate' : 'translate3d'
    const translateIndex = style.indexOf(`transform: ${translateKey}(`)

    if (translateIndex !== -1) {
      const endOfTransform = style.indexOf(')', translateIndex)
      const translateValue = style
        .substring(
          translateIndex + `transform: ${translateKey}(`.length,
          endOfTransform
        )
        .split(',')
      const [x, y] = translateValue.map(
        (val: string) => Number.parseInt(val, 10) * -1
      )

      transformCss = `${translateKey}(${x}px,${y}px`
      if (!L.Browser.ie) {
        transformCss += `,${translateValue[2]}`
      }
      transformCss += ')'
    }

    return transformCss
  }
  private _map_move_function() {
    const map = this._map as unknown as any

    // 获取canvasShipsPane和divCanvasShipsPane元素，并确保它们已初始化
    const canvasShipsPane = map.getPane('canvasShipsPane') as
      | HTMLElement
      | undefined
    const divCanvasShipsPane = map.getPane('divCanvasShipsPane') as
      | HTMLElement
      | undefined

    if (!canvasShipsPane || !divCanvasShipsPane) {
      console.error(
        'Missing required map panes: canvasShipsPane and/or divCanvasShipsPane'
      )
      return
    }

    // 获取当前地图视口尺寸
    const { x: width, y: height } = map.getSize()

    // 设置canvasShipsPane的尺寸
    canvasShipsPane.style.width = `${width}px`
    canvasShipsPane.style.height = `${height}px`

    // 计算并应用初始transform值
    const initialTransformValue = this.getMapTransform(map)
    divCanvasShipsPane.style.transform = initialTransformValue

    // 移动端优化：尝试解决可能出现的渲染偏差
    // 1. 强制触发重排和重绘（在某些情况下可能有助于修正偏差）
    // 假设在应用初始transform之前保存了元素的初始位置
    const initialRect = divCanvasShipsPane.getBoundingClientRect()

    // divCanvasShipsPane.getBoundingClientRect()
    // 2. 使用requestAnimationFrame在下一帧渲染时检查并调整transform值
    requestAnimationFrame(() => {
      // 检查应用初始transform值后是否存在位移
      const rectAfterInitialTransform =
        divCanvasShipsPane.getBoundingClientRect()
      const displacement = {
        x: rectAfterInitialTransform.x - initialRect.x,
        y: rectAfterInitialTransform.y - initialRect.y,
      }

      if (displacement.x !== 0 || displacement.y !== 0) {
        // 调整transform值以补偿位移
        const adjustedTransform = `${initialTransformValue} translate(${-displacement.x}px, ${-displacement.y}px)`
        divCanvasShipsPane.style.transform = adjustedTransform
      }
    })
    // TODO: 新增方式 2024-07-03
    let hasTrackIsShow = false
    map.trackService && (hasTrackIsShow = map.trackService.hasTrackIsShow())
    this.setNoSelShipIsShow(!hasTrackIsShow, false)
  }
  getShipByEvent(event: MouseEvent) {
    return CanvasShipUtils.mouseInArr(
      this._map.getPanes().canvasShipsPane,
      event,
      this._viewShips
    )
  }
  private _mousemoveFunction(event: MouseEvent, _that: this) {
    const ship: Ship | undefined = _that.getShipByEvent(event)
    if (ship) {
      _that._map.getPanes().canvasShipsPane.style.cursor = 'pointer'
      if (ship.datapower !== undefined && ship.datapower == 1) {
        return
      }
      clearTimeout(_that._mouseMoveTimer)
      _that._mouseMoveTimer = setTimeout(() => {
        if (
          (_that._map._hoverShipId &&
            ship.shipid === _that._map._hoverShipId) ||
          (!_that._map._hoverShipId
            ? ((_that._map._hoverShipId = ship.shipid),
              _that._drawShips(),
              _that._openTooltip(event, _that._map, ship))
            : false)
        ) {
          // 如果条件满足则执行_openTooltip等操作
        }
      }, 500)
    } else {
      clearTimeout(_that._mouseMoveTimer)
      _that._closeTooltip()
      _that._map.getPanes().canvasShipsPane.style.cursor = 'inherit'
      if (_that._map._hoverShipId) {
        _that._map._hoverShipId = null
        _that._drawShips()
      }
    }
  }

  private _openTooltip(event: MouseEvent, map: MyMap, ship: any) {
    this._tooltip && CanvasShipUtils.closeTooltip(map, this._tooltip)
    const mouseLatlng = this._getMouseLatlng(event, map)
    const tooltipHtml = this._getTooltipHtml(ship)
    if (!isEmpty(tooltipHtml)) {
      this._tooltip = CanvasShipUtils.openTooltip(
        this._map,
        mouseLatlng,
        tooltipHtml,
        {
          direction: 'auto',
          offset: [0, 0],
          opacity: 0.8,
        }
      )
    }
  }
  _getMouseLatlng(event: MouseEvent, map: MyMap) {
    const mousePos = CanvasShipUtils.getMousePos(
      map.getPanes().canvasShipsPane,
      event
    )
    return map.containerPointToLatLng(new L.Point(mousePos[0], mousePos[1]))
  }
  _getTooltipHtml(ship: CanvasShip) {
    let html = ''
    const tooltipData = this._getTooltipData(ship) as any
    if (isEmpty(this.options.tooltip_fields)) {
      this.options.tooltip_fields = [
        'name',
        'callsign',
        'imo',
        'mmsi',
        'lastdyn',
      ]
    }
    for (let i = 0; i < this.options.tooltip_fields.length; i++) {
      const tooltipField = this.options.tooltip_fields[i]
      let tooltipTxt = ''
      if (
        !(
          isEmpty(tooltipData) ||
          isEmpty(tooltipData[tooltipField]) ||
          isEmpty((this._tooltip_txt_templater as any)[tooltipField])
        )
      ) {
        tooltipTxt = (this._tooltip_txt_templater as any)[tooltipField].replace(
          '{1}',
          tooltipData[tooltipField]
        )
      }
      html += tooltipTxt
    }
    return html
  }
  _getTooltipData(ship: CanvasShip): CanvasShip {
    const shepEntity = ship
    shepEntity.name = ship.name
    if (shepEntity.lastdyn) {
      let lastdynFormat =
        `${shepEntity.lastdyn}`.indexOf('-') > 0 ||
        `${shepEntity.lastdyn}`.indexOf('/') > 0
          ? new Date(shepEntity.lastdyn).getTime()
          : 1000 * shepEntity.lastdyn
      this.options.offsetLastdyn &&
        (lastdynFormat += 1000 * this.options.offsetLastdyn)
      shepEntity.lastdyn = CanvasShipUtils.dataFormat(
        lastdynFormat,
        'YYYY-MM-DD HH:mm:ss'
      ) as any
    }
    shepEntity.navistatus &&
      (shepEntity.navistatus = CanvasShipUtils.getDisValue(
        shepEntity.navistatus,
        'naviStatus',
        'zh_CN'
      ))
    shepEntity.sog && (shepEntity.sog = CommUtils.getNumber(shepEntity.sog, 1))
    // '0' == shepEntity.sog && (shepEntity.sog = 0)
    shepEntity.cog && (shepEntity.cog = CommUtils.getNumber(shepEntity.cog, 1))
    shepEntity.hdg && (shepEntity.hdg = CommUtils.getNumber(shepEntity.hdg, 1))
    shepEntity.rot && (shepEntity.rot = CommUtils.getNumber(shepEntity.rot, 1))
    shepEntity.draught &&
      (shepEntity.draught = CommUtils.getNumber(shepEntity.draught, 1))
    shepEntity.type &&
      (shepEntity.type = CanvasShipUtils.getShipTypeCN(
        shepEntity.type as number
      ))
    shepEntity.newtype &&
      (shepEntity.newtype = CanvasShipUtils.getDisValue(
        shepEntity.newtype,
        'shipType',
        'zh_CN'
      ))
    shepEntity.lat &&
      (shepEntity.lat = CanvasShipUtils.latFormatter(shepEntity.lat) as any)
    shepEntity.lng &&
      (shepEntity.lng = CanvasShipUtils.lngFormatter(shepEntity.lng) as any)
    // '0' == shepEntity.imo && (shepEntity.imo = null)
    shepEntity.seaareaid &&
      (shepEntity.seaareaid = CanvasShipUtils.getDisValue(
        shepEntity.seaareaid,
        'seaArea',
        'zh_CN'
      ))
    return shepEntity
  }

  _closeTooltip() {
    if (this._tooltip) {
      this._map.removeLayer(this._tooltip)
      this._tooltip = null
    }
  }
  _callBackObjBuild(ship: CanvasShip) {
    const shipExtend = ship
    this.options.offsetLastdyn &&
      (shipExtend.lastdyn += this.options.offsetLastdyn)
    return shipExtend
  }
  _updateShips(ships: CanvasShip[]): void {
    if (ships && ships.length !== 0) {
      if (!this._grid_ships.getItemCount()) {
        this._grid_ships.updateDatas(ships, true)
        this._drawShips()
        return
      }

      const beidouShips: CanvasShip[] = []

      for (const ship of ships) {
        if (ship && ship.lat && ship.lng) {
          const shipByPk = this._grid_ships.getItemByPK(ship.shipid)
          if (!CommUtils.isEmpty(shipByPk)) {
            if (
              this._fleetShips &&
              this._fleetShips[shipByPk.mmsi.toString()] &&
              ship.source !== 2
            ) {
              ship.custom_name =
                this._fleetShips[shipByPk.mmsi.toString()].custom_name
              ship.name = this._fleetShips[shipByPk.mmsi.toString()].name
              ship.color = this._fleetShips[shipByPk.mmsi.toString()].color
              ship.istop = this._fleetShips[shipByPk.mmsi.toString()].istop
              ship.group_name =
                this._fleetShips[shipByPk.mmsi.toString()].group_name
              ship.remark = this._fleetShips[shipByPk.mmsi.toString()].remark
              ship.shiptype = 2
            } else if (ship.source !== 2) {
              ship.shiptype = 1
              this._specialShips[shipByPk.shipid] &&
                ((ship.istop = true), (ship.color = shipByPk.color))
            }

            if (this.options.enableTracks) {
              const tracks = shipByPk.tracks || []
              if (tracks.length > 0) {
                const track = tracks[tracks.length - 1]
                const latDiff = Math.abs(track.lat - ship.lat)
                const lngDiff = Math.abs(track.lng - ship.lng)
                if (
                  latDiff > 0 &&
                  lngDiff > 0 &&
                  latDiff <= 0.333 &&
                  lngDiff <= 0.333 &&
                  (!ship.lastdyn_active ||
                    !shipByPk.lastdyn_active ||
                    ship.lastdyn > shipByPk.lastdyn)
                ) {
                  let lastDyn = ship.lastdyn
                  if (track.time > lastDyn && !ship.lastdyn_active) {
                    lastDyn = track.time + 1
                  }
                  shipByPk.tracks.push({
                    lat: ship.lat,
                    lng: ship.lng,
                    time: lastDyn,
                    updtime: Date.now(),
                  })
                } else {
                  console.debug(shipByPk.mmsi, '3分钟轨迹点超出1/3度，丢弃')
                }
              } else {
                shipByPk.tracks.push({
                  lat: ship.lat,
                  lng: ship.lng,
                  time: ship.lastdyn,
                  updtime: Date.now(),
                })
              }
              ship.tracks = []
              const cutOffTime = ship.lastdyn - 180
              for (let j = 0; j < shipByPk.tracks.length; j++) {
                const point = shipByPk.tracks[j]
                if (
                  point.time >= cutOffTime &&
                  (ship.tracks.length === 0 ||
                    ship.tracks[ship.tracks.length - 1].time !== point.time)
                ) {
                  ship.tracks.push(point)
                } else {
                  console.debug(shipByPk.mmsi, '3分钟轨迹点超时，丢弃')
                }
              }
            }

            if (
              ((shipByPk.datapower === 0 || !shipByPk.datapower) &&
                ship.datapower == 1) ||
              (ship.source &&
                shipByPk.source &&
                ship.source !== shipByPk.source &&
                ship.lastdyn < shipByPk.lastdyn)
            ) {
              continue
            }
          }

          const shipMmsi = this.getShipByMmsi(ship.mmsi, false)
          if (shipMmsi) {
            ship.shipid = `${shipMmsi.shipid}`
          }
          const key = `${this._DShips_key}${ship.mmsi}`
          if (key in this._DShips) {
            ship.color = this.options.dShipColor
            ship.istop = true
            ship.shiptype = 2
          }

          if (ship.source === 3 || this._beidou_ships.includes(ship.shipid)) {
            ship.source_real = ship.source
            ship.source = 3
            if (!this._beidou_ships.includes(ship.shipid)) {
              this._beidou_ships.push(ship.shipid)
            }
          }
          beidouShips.push(ship)
        } else {
          console.log('ships 数据非法！', ship)
        }
      }

      this._grid_ships.updateDatas(beidouShips)
      this._drawShips()
    }
  }

  _drawShips(e?: L.LeafletEvent | undefined) {
    const startTime = Date.now()
    this.clearAllShips()
    const topList: CanvasShip[] = []
    let filterList: CanvasShip[] = []
    const selShips: CanvasShip[] = []
    const selShipId = this._map._selShipId
    const hoverShipList: CanvasShip[] = []
    const hoverShipId = this._map._hoverShipId
    const zoom = this._map.getZoom()
    const ItemCount = this._grid_ships.getItemCount()
    console.log(`catch ships total length:${ItemCount}`)
    if (ItemCount) {
      let bounds = this._map.getBounds()
      if (zoom < 12) {
        const slat = bounds.getSouthWest().lat - 0.05
        const slng = bounds.getSouthWest().lng - 0.05
        const nlat = bounds.getNorthEast().lat + 0.05
        const nlng = bounds.getNorthEast().lng + 0.05
        bounds = L.latLngBounds(
          new L.LatLng(slat, slng),
          new L.LatLng(nlat, nlng)
        )
      }
      let drawShips: CanvasShip[] = []
      if (zoom <= this.options.zoomLevel_base) {
        // 特殊船舶
        const specialShipInfo: Record<string, any> = {}
        for (const key in this._specialShips) {
          const ship = this.getShipByShipid(key)
          ship && (specialShipInfo[`${ship.shipid}`] = ship)
        }
        // 舰队船舶
        for (const mmsi in this._fleetShips) {
          const ship = this.getShipByMmsi(mmsi, false)
          if (ship) {
            specialShipInfo[`${ship.shipid}`] = ship
          }
        }
        const dShips = Object.keys(this._DShips)
        for (const dShip of dShips) {
          const ship = this.getShipByMmsi(this._DShips[dShip].mmsi, false)
          if (ship) {
            specialShipInfo[`${ship.shipid}`] = ship
          }
        }

        for (const key in this._map._selShipId) {
          const shipId = this._getKeyShipid_de(key)
          const ship = this.getShipByShipid(shipId)
          specialShipInfo[`${ship?.shipid || shipId}`] = ship
        }
        Object.keys(specialShipInfo).forEach((key) => {
          let lat = specialShipInfo[key].lat
          let lng = specialShipInfo[key].lng
          if (this._map.offsetC) {
            const latLngOffset = GPS.gcj_encrypt(
              lat,
              lng,
              specialShipInfo[key].outCN
            )
            lat = latLngOffset.lat
            lng = latLngOffset.lon
          }
          if (!bounds.contains([lat, lng])) {
            if (bounds.contains([lat, lng + 360])) {
              specialShipInfo[key].offset = 360
            }
            if (bounds.contains([lat, lng - 360])) {
              specialShipInfo[key].offset = -360
            }
          }
          drawShips.push(specialShipInfo[key])
        })
      } else {
        const splitLatLngBounds = CanvasShipUtils.getSplitLatLngBounds(
          bounds,
          zoom
        )
        for (const splitLatLngBound of splitLatLngBounds) {
          const itemByBounds = this._grid_ships.getItemByBounds(
            splitLatLngBound.bounds
          )
          for (const itemByBound of itemByBounds) {
            itemByBound.offset = splitLatLngBound.offset
            drawShips.push(itemByBound)
          }
        }
      }
      console.debug('get catch ships time:', Date.now() - startTime)
      this.options.drawShipsBeforeCallBack &&
        (drawShips = this.options.drawShipsBeforeCallBack(drawShips))
      let isOpenShipsViewFilter = 0
      // 网格大小
      let gridSize = 0.05
      if (
        this.options.drawBaseShipsGridMaxCount > 0 &&
        drawShips.length > this.options.drawBaseShipsGridMaxCount
      ) {
        const index = this.options.drawBaseShipsGridZoom.indexOf(zoom)
        if (index >= 0 && this.options.drawBaseShipsGridMax.length > index) {
          isOpenShipsViewFilter = this.options.drawBaseShipsGridMax[index]
          gridSize = this.options.drawBaseShipsGridSize[index] || 0.05
        }
      }
      // 遍历所有需要绘制的船只
      for (const [i, ship] of drawShips.entries()) {
        const ShipidEn = this._getKeyShipid_en(ship.shipid)
        //  drawShips[key].istop 停止是否渲染
        // 如果当前地图缩放级别大于基础缩放级别，或者特定过滤条件下，继续处理船舶数据
        if (
          !(zoom <= this.options.zoomLevel_base) ||
          (selShipId && ShipidEn in selShipId) ||
          ship.istop
        ) {
          // 创建新的船只对象
          let canvasShip = new CanvasShip()
          canvasShip = L.Util.extend(canvasShip, ship)
          // 如果存在缓存且满足条件，则设置船舶颜色、是否置顶等属性
          if (this._DShips_key + canvasShip.mmsi in this._DShips) {
            canvasShip.color = this.options.dShipColor
            canvasShip.istop = true
            canvasShip.shiptype = 2
          }
          // 计算经纬度并考虑中国偏移
          let latLngOffset = L.latLng(
            canvasShip.lat,
            canvasShip.lng + canvasShip.offset
          ) as any
          // 如果启用了坐标偏移，则进行加密处理
          if (this._map.offsetC) {
            latLngOffset = GPS.gcj_encrypt(
              latLngOffset.lat,
              latLngOffset.lng,
              canvasShip.outCN
            )
            latLngOffset = new L.LatLng(latLngOffset.lat, latLngOffset.lon)
          }
          // 检查船舶位置是否在当前视图范围内
          if (bounds.contains(latLngOffset)) {
            // 确保船只数据有效
            if (canvasShip && canvasShip.lat && canvasShip.lng) {
              canvasShip.arr_index = i
              canvasShip.updateState(this._srvTime)

              const gridShip = this._grid_ships.getItemByPK(canvasShip.shipid)
              gridShip.state = canvasShip.state
              // TODO: 新增功能点
              // if (
              //   !gridShip.is_netsonde ||
              //   (this.options.netsondeOptions &&
              //     this.options.netsondeOptions.show)
              // ) {
              // }
              // 判断是否为"云珍"或"海监"船
              if (!canvasShip.is_yh) {
                canvasShip.is_yh = CanvasShipUtils.isYUNZHENAndHAIJIAN(
                  canvasShip.name,
                  canvasShip.mmsi,
                  canvasShip
                )
              }

              // 根据不同条件选择性绘制船舶
              if (canvasShip.is_yh) {
                // && !(Object.keys(selShipId).length && ShipidEn in selShipId)
                if (Object.keys(selShipId).length && ShipidEn in selShipId) {
                  selShips.push(canvasShip)
                  this._topShipsCount++
                } else if (
                  this._isFilter(canvasShip) &&
                  this._isValidIMO(canvasShip) &&
                  this._noSelShipIsShow
                ) {
                  if (
                    this._areaShipIsShow
                    // (!this._areaShipIsShow && 1 == canvasShip.shiptype) ||
                    // (2 == canvasShip.state && 1 == canvasShip.shiptype)
                  ) {
                    if (hoverShipId && canvasShip.shipid == hoverShipId) {
                      hoverShipList.push(canvasShip)
                    } else if (canvasShip.istop) {
                      topList.push(canvasShip)
                      this._topShipsCount++
                    } else if (zoom > this.options.zoomLevel_base) {
                      this._baseShipsCount++
                      if (isOpenShipsViewFilter > 0) {
                        filterList.push(canvasShip)
                      } else {
                        this._drawShip(canvasShip)
                      }
                    }
                  }
                }
              }
            } else {
              console.log('drawShips ship 数据非法！', canvasShip)
            }
          }
        }
      }
      if (isOpenShipsViewFilter > 0) {
        this._grid_ships_view_filter = new Map<string, any>()
        const lengthBuckets = [
          { name: 'l_90', limit: 90 },
          { name: 'l_80', limit: 80 },
          { name: 'l_70', limit: 70 },
          { name: 'l_60', limit: 60 },
          { name: 'l_50', limit: 50 },
          { name: 'l_40', limit: 40 },
          { name: 'l_30', limit: 30 },
        ]

        filterList.forEach((ship) => {
          const gridKey = CanvasShipUtils.getGridKeyByLatLng(
            ship.lat,
            ship.lng,
            gridSize,
            'g_',
            '_'
          )

          let gridData = this._grid_ships_view_filter.get(gridKey)
          if (!gridData) {
            gridData = {
              ship: [],
              ...lengthBuckets.reduce(
                (acc, { name }) => ({ ...acc, [name]: [] }),
                {}
              ),
            }
            this._grid_ships_view_filter.set(gridKey, gridData)
          }

          const bucket = lengthBuckets.find(({ limit }) => ship.length <= limit)
          if (bucket) {
            gridData[bucket.name].push(ship)
          }
        })

        const uniqueShipIds = new Set()
        const filteredShips: any[] = []

        for (const [, gridData] of this._grid_ships_view_filter) {
          let mergedShips: any[] = []
          for (const { name } of lengthBuckets) {
            gridData[name].some((ship: any) => {
              const shipId = ship.mmsi || ship.shipid
              if (!uniqueShipIds.has(shipId)) {
                mergedShips.push(ship)
                uniqueShipIds.add(shipId)
                if (mergedShips.length >= isOpenShipsViewFilter) {
                  return true // 提前终止循环
                }
              }
              return false
            })

            if (mergedShips.length >= isOpenShipsViewFilter) {
              mergedShips = mergedShips.slice(0, isOpenShipsViewFilter)
              break // 如果达到限制，退出循环
            }
          }
          gridData.ship = mergedShips
          filteredShips.push(...mergedShips)
        }

        filterList = [] // 清空原始列表
        console.debug('区域船抽稀时间:', Date.now() - startTime)
        // requestAnimationFrame(() => {})
        filteredShips.forEach((item) => {
          this._drawShip(item)
        })
        this._baseShipsCount_chouxi = filteredShips.length
      }
      topList.forEach((item) => this._drawShip(item, { isSelected: false }))

      hoverShipList.forEach((item) => {
        this._drawShip(item, {
          lineColor: 'red',
          heightLineColor: 'red',
          isSelected: false,
          radarOptions: {
            lineColor: 'red',
            heightLineColor: 'red',
          },
        })
      })

      selShips.forEach((item) => {
        this._drawShip(item, {
          lineColor: 'red',
          heightLineColor: 'red',
          isSelected: true,
        })
      })
      this._drawLablesFn(this._drawLables)
      this.options.isAutoUpdateSrvtime && this._updateSrvTime()
      setTimeout(() => {
        this._drawShipsEnd(e)
      }, 50)
      console.log(`_drawShips time:${Date.now() - startTime}`)
      console.log(`_drawShips _topShipsCount:${this._topShipsCount}`)
      console.log(`_drawShips _baseShipsCount:${this._baseShipsCount}`)
      console.log(
        `_drawShips _baseShipsCount_chouxi:${this._baseShipsCount_chouxi}`
      )
      console.log(`_drawShips _drawCount:${this._drawCount}`)
    }
  }

  private _drawShipsEnd(event?: L.LeafletEvent): void {
    if (this.options.drawShipsEndCallBack) {
      this.options.drawShipsEndCallBack({
        map: this._map,
        viewShips: this._viewShips,
        count: this._drawCount,
        viewShipLength: this._viewShips.length,
        zoom: this._map.getZoom(),
        event,
      })
    }
  }
  /**
   *  WGS-84：是国际标准，GPS坐标（Google Earth使用、或者GPS模块）
   *  GCJ-02：中国坐标偏移标准，Google Map、高德、腾讯使用
   *  BD-09：百度坐标偏移标准，Baidu Map使用
   */
  // 定义绘制单个船只的方法
  private _drawShip(drawShipItem: CanvasShip, customOptions?: any) {
    if (
      drawShipItem &&
      !Number.isNaN(drawShipItem.lat) &&
      !Number.isNaN(drawShipItem.lng)
    ) {
      let newLatLng: L.LatLng

      // 如果存在地图偏移系数（中国国测局坐标系转换），则进行坐标转换
      if (this._map.offsetC) {
        const WGS84ToGCJ02 = GPS.gcj_encrypt(
          drawShipItem.lat,
          drawShipItem.lng,
          drawShipItem.outCN
        )
        newLatLng = new L.LatLng(WGS84ToGCJ02.lat, WGS84ToGCJ02.lon)
      } else {
        newLatLng = drawShipItem.getLatlng()
      }

      const options = Object.assign({}, this.options)

      // 合并配置选项
      const mergedOptions = cloneDeep(
        merge(options, options.shipOptions, customOptions)
      )
      // 获取或设置颜色回调
      const resetShipColorCallback = options.resetShipColorCallBack
      mergedOptions.color =
        (resetShipColorCallback &&
          resetShipColorCallback.call(options, newLatLng)) ||
        drawShipItem.color ||
        this._clrState[drawShipItem.state]

      // 设置线条和其他样式属性
      mergedOptions.lineColor =
        drawShipItem.lineColor || mergedOptions.lineColor
      mergedOptions.heightLineColor =
        drawShipItem.heightLineColor || mergedOptions.heightLineColor
      mergedOptions.mapType = this._map.mapType
      mergedOptions.labelImages = this._labelImages
      mergedOptions.radarOptions = this.options.radarOptions

      if (customOptions?.radarOptions) {
        mergedOptions.radarOptions = {
          ...mergedOptions.radarOptions,
          ...customOptions.radarOptions,
        }
      }
      // mergedOptions.netsondeOptions = this.options.netsondeOptions
      // if (customOptions.netsondeOptions) {
      //   mergedOptions.netsondeOptions = {
      //     ...mergedOptions.netsondeOptions,
      //     ...customOptions.netsondeOptions,
      //   }
      // }
      if (this.options.shipSizeZoom && this.options.shipSizeZoom > 0) {
        mergedOptions.sizeZoom = this.options.shipSizeZoom || 1
      }

      const canvasShipItem = canvasShipSymbol(
        newLatLng,
        this._map,
        drawShipItem,
        mergedOptions
      )
      canvasShipItem.state = drawShipItem.state

      // 更新现有船只数据或添加新船只
      const existingShip = this._grid_ships.getItemByPK(canvasShipItem.shipid)
      if (existingShip) {
        existingShip.bodyArea = canvasShipItem.bodyArea
        existingShip.x = canvasShipItem.x
        existingShip.y = canvasShipItem.y
        existingShip.head_x = canvasShipItem.head_x
        existingShip.head_y = canvasShipItem.head_y
        existingShip.latlngReal = canvasShipItem.latlngReal
        existingShip.pointCenterReal = canvasShipItem.pointCenterReal
        existingShip.pointCenterGPS = canvasShipItem.pointCenterGPS
        existingShip.rotate = canvasShipItem.rotate
        existingShip.latlng = canvasShipItem.latlng
      }

      delete canvasShipItem.options.isSelected
      this._viewShips.push(merge(drawShipItem, canvasShipItem))
      this._drawCount++
      this._zoomLevel = this._map.getZoom()

      // 绘制船只标签逻辑
      const shipIdEncoded = this._getKeyShipid_en(drawShipItem.shipid)
      if (
        this._zoomLevel >= this.options.weixingLabelZoom ||
        (drawShipItem.istop && this._zoomLevel >= this.options.topLabelZoom) ||
        this._zoomLevel >= this.options.generalLabelZoom ||
        (this._map._selShipId && shipIdEncoded in this._map._selShipId)
      ) {
        let labelText: string
        if ('en' === this.options.labelLanguage) {
          labelText =
            drawShipItem.labelText || drawShipItem.nameen || drawShipItem.name
        } else {
          labelText =
            drawShipItem.labelText ||
            drawShipItem.cnname ||
            drawShipItem.name ||
            '未匹配到名称'
        }

        const showLabelTextWithTime = drawShipItem.isShowLableTime
        if (
          (showLabelTextWithTime ?? this.options.isShowLableTime) &&
          showLabelTextWithTime &&
          !CommUtils.isEmpty(labelText)
        ) {
          const lastUpdateTimestamp = Date.now() - 1000 * drawShipItem.lastdyn
          if (drawShipItem.lastdyn && lastUpdateTimestamp >= 21600000) {
            const dateTime = CanvasShipUtils.dataFormat(
              new Date(1000 * drawShipItem.lastdyn),
              '(MM-DD HH:mm)'
            )
            labelText += dateTime
          }
        }

        // 如果 `drawShipItem.source` 存在且非空，或者 `drawShipItem.source` 等于2
        if (drawShipItem.source === 1 || drawShipItem.source === 2) {
          // 如果 `drawShipItem.datapower` 为真，则清空 `lang` 变量
          if (drawShipItem.datapower === 1) {
            labelText = ''
          }
          // 绘制标签（在这种情况下不依赖于地图缩放级别和选中状态）
          this._drawLabel(canvasShipItem, drawShipItem, labelText)
        } else {
          // 当满足以下条件之一时，绘制标签：
          //   1. 当前地图缩放级别大于等于通用标签显示的最小缩放级别
          //   2. 地图上已选择特定船舶ID，且当前shipEn存在于已选择的船舶ID列表中
          //   3. drawShipItem 的 istop 属性为真，并且当前地图缩放级别大于等于顶级标签显示的最小缩放级别
          if (
            this._zoomLevel >= this.options.generalLabelZoom ||
            (this._map._selShipId && shipIdEncoded in this._map._selShipId) ||
            (drawShipItem.istop && this._zoomLevel >= this.options.topLabelZoom)
          ) {
            this._drawLabel(canvasShipItem, drawShipItem, labelText)
          }
        }
      }

      return canvasShipItem
    }
  }

  // 定义用于绘制船只标签的方法
  _drawLabel(shipInfo: any, Config: CanvasShip, labelText: string): void {
    // 检查是否应显示标签，优先级：传入配置 > 默认配置
    const showLabel = !Config.isShowLabel
      ? this.options.isShowLabel
      : Config.isShowLabel
    const validSource =
      Config.source !== null && ![1, 2, 3, 0, 'shipxy'].includes(Config.source)
    const hasLabelText = !isEmpty(labelText)
    const shouldShowLabel =
      showLabel && shipInfo && Config && !validSource && hasLabelText
    if (shouldShowLabel) {
      let avoidOverlap = true
      const point = new L.Point(shipInfo.x, shipInfo.y)
      // 判断该船只ID是否在选中船只集合内，如果是，则避免标签重叠
      if (this._getKeyShipid_en(Config.shipid) in this._map._selShipId) {
        avoidOverlap = false
      }

      // 获取船只身体区域的边界框面积
      const boundingBoxArea = CanvasShipUtils.getBoundingBoxArea(
        shipInfo.bodyArea
      )
      let minX = 0,
        minY = 0,
        maxX = 0,
        maxY = 0
      if (boundingBoxArea && boundingBoxArea.min) {
        minX = boundingBoxArea.min.x
        minY = boundingBoxArea.min.y
      }
      if (boundingBoxArea && boundingBoxArea.max) {
        maxX = boundingBoxArea.max.x
        maxY = boundingBoxArea.max.y
      }

      // 计算船只包围盒的半径（假设为圆形）
      const radius = Math.round(
        Math.sqrt(
          (Math.abs(maxX - minX) / 2) ** 2 + (Math.abs(maxY - minY) / 2) ** 2
        )
      )
      const sourceType = Config.source
      let font = '600 12px Arial'
      let txtColor = '#000000'
      let lineColor = '#000000'
      let lineFillColor = null
      let obliqueLineColor = null
      let labelRotate = null

      // 合并默认选项与传入配置的选项
      const mergedOptions = merge(this.options, Config.options) as any

      // 根据地图类型选择相应的标签样式
      if (
        mergedOptions.lableTxtColorMapTypeDark.includes(
          this._map.mapType as string
        )
      ) {
        // 使用暗色主题样式
        font = mergedOptions.lableFont[1]
        txtColor = mergedOptions.lableTxtColor[1]
        lineColor = mergedOptions.lableLineColor[1]
        lineFillColor = mergedOptions.lableLinefillColor[1]
        obliqueLineColor = mergedOptions.obliqueLineColor[1]
        labelRotate = mergedOptions.lableRotate[1]
      } else {
        // 使用亮色主题样式
        font = mergedOptions.lableFont[0]
        txtColor = mergedOptions.lableTxtColor[0]
        lineColor = mergedOptions.lableLineColor[0]
        lineFillColor = mergedOptions.lableLinefillColor[0]
        obliqueLineColor = mergedOptions.obliqueLineColor[0]
        labelRotate = mergedOptions.lableRotate[0]
      }

      // 如果配置中有自定义文本颜色，则覆盖默认或暗/亮色主题的颜色
      if (!isEmpty(Config.txt_color)) {
        txtColor = Config.txt_color
      }

      // 获取标签对应的图片信息
      const labelImage = this._getLabelImageInfo(shipInfo._ship)

      // 计算标签布局信息
      const calculatedLabel = CanvasShipUtils.calcLabel(
        this._map,
        point,
        labelText,
        this._labels,
        radius,
        avoidOverlap,
        font,
        labelImage
      )

      // 如果成功计算出标签布局，则将其添加到待绘制队列和全局标签列表中
      if (calculatedLabel) {
        this._drawLables.push({
          symbol: shipInfo,
          calcLabel: calculatedLabel,
          sourceType,
          labelImage,
          labelText,
          txtColor,
          font,
          lineColor,
          lineFillColor,
          obliqueLineColor,
          lableRotate: labelRotate,
        })
        this._labels.push(calculatedLabel)
      }
    }
  }
  _drawLablesFn(drawLables: string | any[]) {
    if (drawLables)
      for (const drawLable of drawLables) {
        drawLable.symbol.drawLable(
          drawLable.calcLabel,
          drawLable.labelImage,
          drawLable.labelText,
          drawLable.txtColor,
          drawLable.font,
          drawLable.labelRotate,
          drawLable.lineColor,
          drawLable.lineFillColor,
          drawLable.obliqueLineColor
        )
      }
  }

  /**
   * 获取绘制label图标信息
   * @param shipItem 船舶对象
   * @param imgSize 图标大小
   * @param imgOffset 偏移量
   * @returns {isDraw:是否绘制,imgTypes:satellite:卫星|beidou:北斗|monitor:监控|monitor_nosign:监视器无信号,imgWidth:图标宽度,imgSize:图标大小,imgOffset:偏移量}
   */
  _getLabelImageInfo(
    shipItem: CanvasShip,
    imgSize = 16,
    imgOffset = 4
  ): {
    isDraw: boolean
    imgTypes: (
      | 'common'
      | 'satellite'
      | 'dplus'
      | 'beidou'
      | 'monitor'
      | 'monitor_nosign'
    )[]
    imgWidth: number
    imgSize: number
    imgOffset: number
  } {
    const isMonitor = shipItem.is_monitor
    const isOline = this._checkShipIsMonitor(shipItem, true)

    const imgTypes: (
      | 'common'
      | 'satellite'
      | 'dplus'
      | 'beidou'
      | 'monitor'
      | 'monitor_nosign'
    )[] = []

    switch (shipItem.source) {
      case 0:
        if (this._labelImages.common) {
          imgTypes.push('common')
        }
        break
      case 1:
        if (this._labelImages.satellite) {
          imgTypes.push('satellite')
        }
        break
      case 2:
        if (this._labelImages.dplus) {
          imgTypes.push('dplus')
        }
        break
      case 3:
        if (this._labelImages.beidou) {
          imgTypes.push('beidou')
        }
        break
    }

    if (isMonitor && this._labelImages.monitor) {
      imgTypes.push(isOline ? 'monitor' : 'monitor_nosign')
    }

    const drawLength = imgTypes.length
    const imgWidth = drawLength * (imgOffset + imgSize)

    return {
      isDraw: drawLength > 0,
      imgTypes,
      imgWidth,
      imgSize,
      imgOffset,
    }
  }

  /**
   * 设置是否显示label
   * @param isShowLabel 是否显示label
   */
  setShowLabel(isShowLabel: boolean) {
    this.options.isShowLabel = isShowLabel
    this._drawShips()
  }
  _updateSrvTime(time?: number) {
    this._srvTime = !time
      ? Math.max(this._srvTime, Date.now() / 1000)
      : Math.max(this._srvTime, time)
  }
  timerFunction(_this: this) {
    if (
      !_this.options.enableFleetShip &&
      !_this.options.enableAreaShip &&
      !_this.options.enableDShip &&
      !_this.options.enableSelectedShip
    ) {
      _this.options.enableDShip && _this._requestDShip()
      _this.options.enableFleetShip && _this._requestFleetShips_v3()
      if (_this.options.enableAreaShip) {
        _this._continue = 0
        if (_this._timer_area_num) {
          _this._timer_area_num = 0
          _this._requestAreaShips_v3()
        } else {
          _this._timer_area_num = 1
        }
        !_this.options.enableSelectedShip && _this._requestSelectedShips()
      }
      _this.options.enableSelectedShip && _this._requestSelectedShips()
      _this._requestGreenShips()
      if (_this._requestAreaShips && _this._requestAreaShips.length > 0) {
        _this._drawRequestShips(_this)
      } else {
        setTimeout(_this._drawRequestShips, 800, _this)
        if (_this._timerOpen) {
          _this._map.getZoom() <= _this.options.zoomLevel_base
            ? (_this._delayTime = 60000)
            : (_this._delayTime = Math.max(5000, _this.options.delayTime))
          _this._timer && clearTimeout(_this._timer)
          _this._timer = setTimeout(
            _this.timerFunction,
            _this._delayTime,
            _this
          )
        }
        console.log('ships timer runed!')
      }
    }
  }
  _drawRequestShips(_that: this) {
    const areaShips = _that._requestAreaShips
    _that._requestAreaShips.splice(0, areaShips.length)
    _that.options.drawRequestShipsBeforeCallback &&
      _that.options.drawRequestShipsBeforeCallback(areaShips)
    _that._updateShips(areaShips)
    if (_that._selectedShipUpdateListener && _that._map._selShipId) {
      for (const id in _that._map._selShipId) {
        const shipId = _that._getKeyShipid_de(id)
        const ship = _that.getShipByShipid(shipId)
        if (ship) {
          _that._selectedShipUpdateListener(_that._callBackObjBuild(ship))
          break
        }
      }
    }
  }
  _timerStart() {
    this._timerStop()
    this._timerOpen = true
    this.timerFunction(this)
  }
  _timerStop() {
    this._timerOpen = false
    if (this._timer) {
      clearTimeout(this._timer)
      this._timer = null
    }
  }
  _isValidIMO(ship: CanvasShip) {
    return (
      !ship ||
      !this._filterOptions ||
      !this._filterOptions.validIMO ||
      !this._filterOptions.validIMO ||
      (!isEmpty(ship.imo) && /[1-9](\d){6}/.test(ship.imo))
    )
  }
  /**
   * 条件判定是否显示不显示
   * @param ship 船舶信息
   * @returns
   */
  _isFilter(ship: CanvasShip): boolean {
    let isFiltered = false
    let shipType: string = CanvasShipUtils.getShipTypeCN(ship.type as any)
    ship.newtype &&
      (shipType = CanvasShipUtils.getNewShipTypeCN(
        Number.parseInt(ship.newtype)
      ))
    const length = ship.length || 0
    const hdg = ship.hdg || 511
    const sog = ship.sog || 0
    // const navistatus = ship.navistatus?.toString() || ''

    if (this._filterOptions) {
      let isLengthMatched = false
      let isHdgMatched = false
      let isSogMatched = false
      let isCountryMatched = false

      const shipTypeStr = `,${this._filterOptions.shipType},`
      const shipLength = this._filterOptions.shipLength
      const shipHdg = this._filterOptions.shipHdg
      const shipSog = this._filterOptions.shipSog
      // const shipNaviStatus = this._filterOptions.shipNaviStatus
      const shipCountry = this._filterOptions.shipCountry

      if (ship.istop) return true
      if (!shipType) shipType = '其他'

      if (
        !(
          this._filterOptions.shipType === undefined ||
          !this._filterOptions.shipType ||
          (shipType &&
            shipType.length > 0 &&
            shipTypeStr.includes(`,${shipType},`))
        )
      ) {
        return false
      }

      if (shipLength.length > 0) {
        for (const element of shipLength) {
          const minLen = element[0]
          const maxLen = element[1]
          if (length >= minLen && length <= maxLen) {
            isLengthMatched = true
            break
          }
        }
      } else {
        isLengthMatched = true
      }

      if (!isLengthMatched) return false

      if (shipHdg.length > 0) {
        for (const element of shipHdg) {
          const minHdg = element[0]
          const maxHdg = element[1]
          if (hdg >= minHdg && hdg <= maxHdg) {
            isHdgMatched = true
            break
          }
        }
        if (!isHdgMatched) return false
      } else {
        isHdgMatched = true
      }

      if (
        this._filterOptions.shipCustomSog &&
        this._filterOptions.shipCustomSog.length > 0
      ) {
        for (let i = 0; i < this._filterOptions.shipCustomSog.length; i++) {
          const sogRange = this._filterOptions.shipCustomSog[i]
          if (sog >= sogRange[0] && sog <= sogRange[1]) {
            isSogMatched = true
            break
          }
        }
        if (!isSogMatched) return false
      } else {
        let minSog = 0
        let maxSog = 9999
        const shipSogs = shipSog.split(',').filter(Boolean)

        if (shipSogs.length === 2) {
          minSog = 0
          maxSog = 9999
          isSogMatched = true
        } else if (shipSogs.includes('1')) {
          minSog = 1
          maxSog = 9999
        } else if (shipSogs.includes('0')) {
          minSog = 0
          maxSog = 1
        } else {
          minSog = 0
          maxSog = 9999
          isSogMatched = true
        }

        if ((!isSogMatched && sog < minSog) || sog >= maxSog) return false
        isSogMatched = true
      }

      // TODO: 根据navistatus 进行过滤
      // const naviStatusList = shipNaviStatus.split(',').filter((item) => !item)
      // if (naviStatusList.length > 0 && !naviStatusList.includes(navistatus))
      //   return false

      if (isNull(ship.country)) {
        ship.country = CanvasShipUtils.isCNShip(ship.mmsi)
      }

      if (shipCountry.length > 0) {
        const countryCodes = shipCountry.split(',')
        isCountryMatched =
          !isNull(ship.country) && countryCodes.includes(`${ship.country}`)
        if (!isCountryMatched && !isEmpty(ship.mmsi)) {
          isCountryMatched = countryCodes.includes(`${ship.mmsi}`.slice(0, 3))
        }
      } else {
        isCountryMatched = true
      }

      if (!isCountryMatched) return false

      isFiltered =
        isLengthMatched && isHdgMatched && isSogMatched && isCountryMatched
    } else {
      isFiltered = true
    }

    return isFiltered
  }
  /**
   * 添加船舶
   *  添加船舶，更新缓存，并绘制。
   * @params ships	Array	船舶数组，[CanvasShip]，船舶属性必填项有：shipid、mmsi、lat、lng
   */
  addShips(ships: CanvasShip[]) {
    if (ships) {
      if (this.options.isAutoUpdateSrvtime) {
        this._updateSrvTime()
      }

      for (const ship of ships) {
        // 确保船舶MMSI有效
        if (ship.mmsi && Number.parseInt(ship.mmsi) >= 1) {
          // 判断是否为"云珍"或"海监"船舶
          if (typeof ship.is_yh === 'undefined') {
            ship.is_yh = CanvasShipUtils.isYUNZHENAndHAIJIAN(
              ship.name,
              ship.mmsi,
              ship
            )
          }

          // 判断船舶是否在中国境外
          if (typeof ship.outCN === 'undefined') {
            ship.outCN = GPS.outOfChina(ship.lat, ship.lng)
          }

          // 设置最后动态时间戳
          if (typeof ship.lastdyn === 'undefined') {
            ship.lastdyn = Date.now() / 1000
          }

          // 若船舶类型未定义，则设置为默认值1
          if (CommUtils.isEmpty(ship.shiptype)) {
            ship.shiptype = 1
          }

          // 根据船舶类型处理舰队船舶
          if (ship.shiptype === 2) {
            const mmsiStr = ship.mmsi.toString()
            if (!(mmsiStr in this._fleetShips)) {
              this._fleetShips[mmsiStr] = ship
            }
          } else {
            const key = this._DShips_key + ship.mmsi
            if (
              ship.mmsi.toString() in this._fleetShips ||
              key in this._DShips
            ) {
              ship.shiptype = 2
            }
          }
        }
      }

      this._updateShips(ships)
    }
  }
  /**
   * 删除船通过船舶ID
   *  除船通过船舶ID，船舶ID是，字符串，或逗号分割的字符串，或数组。
   * @params shipsid	string | array	船舶ID(字符串|逗号分割的字符串|数组)
   */
  deleteShipByShipID(shipsid: string | string[]) {
    let shipIds: any[] = []
    if (!isEmpty(shipsid)) {
      shipIds =
        'array' == CommUtils.getObjType(shipsid)
          ? shipIds.concat(shipsid)
          : (shipsid as string).split(',')
      if (shipIds) {
        for (const shipId of shipIds) {
          this._grid_ships.deleteByPK(shipId)
        }
      }
      this._drawShips()
    }
  }
  /**
   * 删除所有船舶
   */
  removeAllShips() {
    this._scode = 0
    this._grid_ships.deleteAll()
    this._fleetShips && (this._fleetShips = {})
    this._drawShips()
  }
  /**
   * 添加关注船
   * @params ships	Array	船舶数组，[CanvasShip]，船舶属性必填项有：shipid、mmsi、lat、lng
   */
  addFleetShips(ships: CanvasShip[]) {
    if (ships) {
      const fleetShips: CanvasShip[] = []
      for (const ship of ships) {
        ship.shiptype = 2
        ship.istop = true
        fleetShips.push(ship)
      }
      this.addShips(fleetShips)
    }
  }
  /**
   * 删除指定的关注船
   * @params ships	Array	船舶mmsi数组，为空则删除所有关注船
   */
  deleteFleetShips(ships: string[] = []) {
    this._fleetShips && (ships = Object.keys(this._fleetShips))
    for (const ship of ships) {
      ship in this._fleetShips && delete this._fleetShips[ship]
      const shipByMMSI = this._grid_ships.getItemByMMSI(ship)
      const shipKey = this._DShips_key + ship
      if (!(isEmpty(shipByMMSI) || shipKey in this._DShips)) {
        shipByMMSI.shiptype = 1
        shipByMMSI.color = undefined
        shipByMMSI.istop = false
        shipByMMSI.custom_name = undefined
      }
    }
    this._drawShips()
  }
  /**
   * 删除所有关注船船舶
   */
  deleteAllFleetShips() {
    this._fleetShips && this.deleteFleetShips()
  }
  /**
   * 删除所有区域船船舶
   */
  deleteAllAreaShips() {
    for (const item of this._grid_ships._items) {
      if (item.shiptype) {
        this._grid_ships.deleteByPK(item.shipid)
      }
    }
    this._drawShips()
  }
  clearAllShips() {
    this._drawCount = 0
    this._topShipsCount = 0
    this._baseShipsCount = 0
    this._baseShipsCount_chouxi = 0
    this._viewShips = []
    this._labels = []
    this._drawLables = []
    const canvasShipsPane = this._map.getPanes().canvasShipsPane
    if (canvasShipsPane) {
      const context = (canvasShipsPane as any).getContext(
        '2d'
      ) as CanvasRenderingContext2D
      context.clearRect(
        0,
        0,
        Number.parseInt(canvasShipsPane.style.width),
        Number.parseInt(canvasShipsPane.style.height)
      )
      context.save()
    }
  }
  /**
   * 船舶定位
   * @params mmsi	String	mmsi
   * @params isQueryURL	String	缓存查不到，是否查询数据接口,默认：true
   */
  locationShip(mmsi: string, isQueryURL = true, shipInfo?: CanvasShip) {
    if (isNull(mmsi)) return null
    const shipByMmsi = shipInfo
      ? shipInfo
      : this.getShipByMmsi(mmsi, isQueryURL)
    if (shipByMmsi) {
      this._map._selShipId = {}
      const key = this._getKeyShipid_en(shipByMmsi.shipid)
      this._map._selShipId[`${key}`] = shipByMmsi.shipid
      this._updateShips([shipByMmsi])
      let latLng = L.latLng(shipByMmsi.lat, shipByMmsi.lng)
      latLng = CanvasShipUtils.GPSEncryptByMapToLatLng(latLng, this._map)
      this._map.getZoom() < 9
        ? this._map.setView(latLng, 9)
        : this._map.setView(latLng)
      if (this._selectedShipListener) {
        setTimeout(() => {
          this._selectedShipListener &&
            this._selectedShipListener(this._callBackObjBuild(shipByMmsi))
        }, 50)
      }

      return !shipByMmsi ? null : shipByMmsi
    }
    return null
  }
  /**
   * 获取船舶信息(通过MMSI)
   * @params mmsi	String	mmsi
   * @params isQueryURL	boolean	缓存中查不到数据，是否查询数据接口,默认：true
   */
  getShipByMmsi(mmsi: string, isRequest: boolean): CanvasShip | null {
    let itemByMMSI = null
    if (isNull(mmsi)) return null
    if (!isRequest) itemByMMSI = this._grid_ships.getItemByMMSI(mmsi)
    else {
      if (isRequest) {
        const ship = this._requestShipByMmsi(mmsi)
        if (ship.length > 0) {
          itemByMMSI = ship[0] // 在检查长度大于0之后访问，不会触发错误
        }
      } else {
        itemByMMSI = this._grid_ships.getItemByMMSI(mmsi)
        if (!itemByMMSI) {
          const ship = this._requestShipByMmsi(mmsi)
          if (ship.length > 0) {
            itemByMMSI = ship[0]
          }
        }
      }
    }
    return !itemByMMSI ? null : itemByMMSI
  }
  /**
   * 获取船舶信息(通过船舶ID)
   * @params shipid	String	船舶ID
   */
  getShipByShipid(shipid: string | number) {
    const ship = this._grid_ships.getItemByPK(shipid)
    return isEmpty(shipid) || !ship ? null : ship
  }
  /**
   * 设置船舶筛选条件
      1、船舶类型
      2、航速
      3、船舶长度
      4、船舶国籍
      5、IMO是否合法
   * @params options	Object	过滤设置，满足设置的条件将显示船舶，否则不现实
   */
  setFilter(options: any) {
    L.Util.extend(this._filterOptions, options)
    this._drawShips()
  }
  /**
   * 获取船舶过滤条件
   */
  getFilter() {
    return this._filterOptions
  }
  /**
   * 添加船舶右键事件
   *  添加船舶右键事件
   * @Event listener	function	回调方法，返回参数：{"shipid":"船舶唯一标识","mmsi":"船舶的mmsi"}
   */
  addRightContextmenuListener(
    listener: (ship: Ship | null, style?: Record<string, any>) => void
  ) {
    this._rightContentMenu = listener
  }
  /**
   * 移除船舶右键事件
   */
  removeRightContextmenuListener() {
    this._rightContentMenu && (this._rightContentMenu = null)
  }
  /**
   * 添加选中船舶事件
   *  添加选中船舶事件，选中船舶时触发该事件
   * @Event listener	function	回调方法，返回参数：{"shipid":"船舶唯一标识","mmsi":"船舶的mmsi"}
   */
  addSelectedListener(listener: (params: any) => void) {
    this._selectedShipListener = listener
  }
  /**
   * 移除选中船舶事件
   */
  removeSelectedListener() {
    this._selectedShipListener && (this._selectedShipListener = null)
  }
  /**
   * 未选中船舶事件
   * @Event listener	function	回调方法
   */
  addUnSelectedListener(listener: (params?: any) => void) {
    this._map._unselectedShipListener = listener
  }
  /**
   * 移除未选中船舶事件
   */
  removeUnSelectedListene() {
    this._map._unselectedShipListener &&
      (this._map._unselectedShipListener = undefined)
  }
  /**
   * 添加选中船数据刷新事件
   * @Event listener	function	回调方法
   */
  addSelectedShipUpdateListener(listener: (params?: any) => void) {
    this._selectedShipUpdateListener = listener
  }
  /**
   * 取消添加选中船数据刷新事件
   */
  removeSelectedShipUpdateListener() {
    this._selectedShipUpdateListener &&
      (this._selectedShipUpdateListener = null)
  }

  /**
   * 获取区域船显示状态
   */
  getAreaShipShowStatus() {
    return this._areaShipIsShow
  }
  /**
   * 设置区域船显示状态
   * @params isShow	Boolean	区域船是否可用
   */
  setAreaShipShowStatus(isShow: boolean) {
    this._areaShipIsShow = isShow
    this._drawShips()
  }
  /**
   * 非选中船是否显示
   *  非选中船是否显示，并执行重绘
   * @params isShow	boolean	是否显示
   * @params isRedrawShips	boolean	是否重绘区域船
   */
  setNoSelShipIsShow(isShow: boolean, isRedrawShips: boolean) {
    this._noSelShipIsShow = isShow
    isRedrawShips && this._drawShips()
  }
  /**
   * 是否相应鼠标事件
   * @params isEvent	boolean	true:响应，false：不响应；
   */
  setPointerEvents(isEvent: boolean) {
    if (this._map) {
      const panes = this._map.getPanes()
      if (isEvent) {
        if (hasIn(panes, 'canvasShipsPane')) {
          panes.canvasShipsPane.style.pointerEvents = 'auto'
        }
      } else {
        if (hasIn(panes, 'canvasShipsPane')) {
          panes.canvasShipsPane.style.pointerEvents = 'none'
        }
      }
    }
  }
  /**
   * 设置选中船
   * @params shipid	Object	船舶shipId字符串或{shipid:shipid}对象
   */
  setSelectedShip(shipid: any) {
    if (!CanvasShipUtils.isEmpty(shipid)) {
      if ('object' === CanvasShipUtils.getObjType(shipid))
        this._map._selShipId = shipid
      else {
        this._map._selShipId = {}
        const key = this._getKeyShipid_en(shipid)
        this._map._selShipId[`${key}`] = shipid
        const ship = this.getShipByShipid(shipid)
        ship &&
          this._selectedShipListener &&
          setTimeout(() => {
            this._selectedShipListener &&
              this._selectedShipListener(this._callBackObjBuild(ship))
          }, 50)
      }
      this._drawShips()
    }
  }
  /**
   * 获取选中船
   */
  getSelectedShip() {
    return this._map._selShipId
  }
  /**
   * 取消选中船
   */
  cancelSelectedShip() {
    this._map._selShipId = {}
    this._drawShips()
  }

  /**
   * 设置船舶绘制图层层级
   * @params zindex	number	层级
   */
  setZIndex(zindex = 90) {
    if (this._map) {
      const panes = this._map.getPanes()
      if (hasIn(panes, 'divCanvasShipsPane')) {
        panes.divCanvasShipsPane.style.zIndex = `${zindex}`
      }
    }
  }
  /**
   * 获取船舶绘制图层层级
   *  获取船舶绘制图层层级(如不存在返回null)
   */
  getZIndex() {
    return this._map
      ? this._map.getPanes().divCanvasShipsPane.style.zIndex
      : null
  }
  /**
   * 恢复默认船舶绘制图层层级
   */
  restoreZIndex() {
    if (this._map) {
      let isRestore = false
      this._map.trackService &&
        (isRestore = this._map.trackService.hasTrackIsShow())
      if (!isRestore) {
        this._map.getPanes().divCanvasShipsPane.style.zIndex = '250'
        this._map_move_function()
        this._drawShips()
      }
    }
  }
  setOffsetLastdyn(offsetLastdyn: number) {
    this.options.offsetLastdyn = offsetLastdyn
    return this.options.offsetLastdyn
  }
  // TODO: 网络请求
  _requestAreaShips_v3() {
    console.log('网络请求')
  }
  _requestDShip() {
    console.log('_requestDShip')
  }
  _requestFleetShips_v3() {
    console.log('_requestFleetShips_v3')
  }
  _requestShipByMmsi(mmis: string) {
    console.log('_requestShipByMmsi', mmis)
    return ['none'] as any
  }
  _requestSelectedShips() {
    console.log('_requestSelectedShips')
  }
  _requestGreenShips() {
    console.log('_requestGreenShips')
  }
  _getKeyShipid_en(shipId: string | number) {
    return Number.parseInt(`${shipId}`) == shipId
      ? `${shipId}_selShipId`
      : shipId
  }
  _getKeyShipid_de(id: string) {
    return `${id}`.replace('_selShipId', '')
  }
  /**
   * 更新设置的监控船列表
   * @params monitorShips		监控船列表
   * @params isAdd		是否追加 默认不追加，清除原有设置
   */
  updateMonitorShips(monitorShips: CanvasShip[]) {
    this._monitorShipsHash = {}
    for (let i = 0; i < monitorShips.length; i++)
      this._monitorShipsHash[monitorShips[i].mmsi] = monitorShips[i]
    return this.getMonitorShips()
  }
  /**
   * 获取设置的监控船舶列表
   */
  getMonitorShips() {
    this.options.monitorShips = Object.keys(this._monitorShipsHash)
    return this.options.monitorShips
  }
  _checkShipIsMonitor(ship: CanvasShip, isHash = false) {
    const { mmsi, monitorOnline } = ship
    return (
      monitorOnline ||
      (!!mmsi &&
        mmsi.toString() in this._monitorShipsHash &&
        (!isHash || this._monitorShipsHash[mmsi].online))
    )
  }
  /**
   * 添加特殊船舶（置顶显示）
   */
  addSpecialShips(specialShips: string | any[]) {
    if (specialShips) {
      const ships: CanvasShip[] = []
      for (const ship of specialShips) {
        ship.istop = true
        ships.push(ship)
        this._specialShips[`${ship.shipid}`] = ship
      }
      this.addShips(ships)
    }
  }
  /**
   * 根据MMSI删除指定特殊船
   * @params ships		特殊船舶MMSI
   */
  deleteSpecialShips(isClearCache = false, ships?: string | string[]) {
    ships = ships || Object.keys(this._specialShips)
    const deleteShipIds: string[] = []
    for (const shipid of ships) {
      let mmsi = ''
      if (shipid in this._specialShips) {
        mmsi = this._specialShips[shipid].mmsi
        delete this._specialShips[shipid]
        deleteShipIds.push(shipid)
      }
      const ship = this._grid_ships.getItemByPK(shipid)
      if (mmsi) {
        const key = this._DShips_key + mmsi
        isEmpty(ship) ||
          key in this._DShips ||
          mmsi in this._fleetShips ||
          (ship.istop = false)
      }
    }
    isClearCache && deleteShipIds.length
      ? this.deleteShipByShipID(deleteShipIds)
      : this._drawShips()
  }
}

// const ShipService = (
//   map: MyMap,
//   options?: CanvasShipServiceOptions
// ) => {
//   map.shipsService || (map.shipsService = new CanvasShipService(map, options))
//   return map.shipsService
// }

// export default ShipService
export { CanvasShipService }
